import { useState, useMemo } from 'react';
import { TestResult } from '../types';

export interface FilterOptions {
  searchTerm: string;
  patientId: string;
  testCode: string;
  category: string;
  dateFrom: string;
  dateTo: string;
}

export function useResultsFilter(results: TestResult[]) {
  const [filters, setFilters] = useState<FilterOptions>({
    searchTerm: '',
    patientId: '',
    testCode: '',
    category: '',
    dateFrom: '',
    dateTo: '',
  });

  // Extract unique values for dropdowns
  const uniquePatients = useMemo(() => {
    const patients = new Map<string, string>();
    results.forEach(r => {
      if (r.patientId && r.patientName) {
        patients.set(r.patientId, r.patientName);
      }
    });
    return Array.from(patients.entries()).map(([id, name]) => ({ id, name }));
  }, [results]);

  const uniqueTests = useMemo(() => {
    const tests = new Set(results.map(r => r.testCode));
    return Array.from(tests).sort();
  }, [results]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set(results.map(r => r.testCategory));
    return Array.from(categories).sort();
  }, [results]);

  // Filter results based on current filters
  const filteredResults = useMemo(() => {
    return results.filter(result => {
      // Search term (searches in patient name, test code, and notes)
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          result.patientName?.toLowerCase().includes(searchLower) ||
          result.testCode.toLowerCase().includes(searchLower) ||
          result.notes?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Patient filter
      if (filters.patientId && result.patientId !== filters.patientId) {
        return false;
      }

      // Test code filter
      if (filters.testCode && result.testCode !== filters.testCode) {
        return false;
      }

      // Category filter
      if (filters.category && result.testCategory !== filters.category) {
        return false;
      }

      // Date range filter
      if (filters.dateFrom || filters.dateTo) {
        const resultDate = new Date(result.date);

        if (filters.dateFrom) {
          const fromDate = new Date(filters.dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (resultDate < fromDate) return false;
        }

        if (filters.dateTo) {
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (resultDate > toDate) return false;
        }
      }

      return true;
    });
  }, [results, filters]);

  const updateFilter = (key: keyof FilterOptions, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      patientId: '',
      testCode: '',
      category: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value !== '');
  }, [filters]);

  return {
    filters,
    filteredResults,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    uniquePatients,
    uniqueTests,
    uniqueCategories,
  };
}
